import { useEffect, useRef, useState } from "react";
import getBackgroundColor from "helpers/getBackgroundColor.js";
import AnimationContainer from "components/Reusable/AnimationContainer/AnimationContainer.js";
import Icon from "components/Elements/Icon/Icon.tsx";
import useMediaQueries from "hooks/useMediaQueries.js";
import useOnClickOutside from "hooks/useOnClickOutside.js";
import Button from "components/Elements/Button/Button.js";
import VerificationContainer from "../../Modals/VerificationContainer.js";
import Image from "components/Elements/Image/Image.tsx";
import ContactNameElem from "./ContactNameElem.tsx";
import useAddContact from "components/Pages/ProfileUser/Contacts/useAddContact.ts";
import useShowManageContactsModal from "../../hooks/useShowManageContactsModal.ts";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import { useCookie } from "contexts/cookieContext/cookieContext.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import { LinkToNewTab } from "components/Elements/Link/Link.js";
import { useUploader } from "contexts/uploaderContext/uploaderContext.js";
import { useUser } from "contexts/userContext/userContext.js";
import { ManageContactsToolltipModal } from "../../Modals/ManageContactsToolltipModal.js";

interface IContactItem {
  item?:
    | string
    | {
        contact_list_email: string;
        contact_list_full_name: string;
        contact_list_image: string;
      };
  index: number;
  drag: string;
  empty: boolean;
  settingItem: boolean;
  settingsClickCallback: (arg0: boolean) => void;
  onContactAdded: () => void;
  showPromo?: boolean;
}

const ButtonBody = () => {
  const { getTextByKey } = useContent();
  const { isLoggedIn } = useUser();

  return (
    // @ts-ignore
    <Tooltip
      tooltipText={getTextByKey("empty-contact-home-tooltip")}
      bottom
      margin={10}
    >
      {!isLoggedIn() ? (
        <div className="not-added-contact contact-img">
          <Icon name="user" white />
        </div>
      ) : (
        <div className="contact-img">
          <Icon name="plus" white />
          <Icon name="user" white />
        </div>
      )}
      <div className="contact-name"></div>
    </Tooltip>
  );
};

const ContactItem = ({
  item = {
    contact_list_email: "",
    contact_list_full_name: "",
    contact_list_image: "",
  },
  index,
  drag,
  empty,
  settingItem,
  settingsClickCallback,
  onContactAdded,
  showPromo,
}: IContactItem) => {
  const { getTextByKey } = useContent();
  const { setRecipientEmail } = useUploader();
  const { mobileCondition } = useMediaQueries();
  const { setModal, getModal, closeModal } = useModal();
  const { getCookie, setCookies } = useCookie();
  const { isLoggedIn } = useUser();

  const [mounted, setMounted] = useState(false);
  const [hover, setHover] = useState<boolean>(false);

  const addContact = useAddContact(onContactAdded);

  const { shouldShowManageContactsModal, incTodaysViewCounts } =
    useShowManageContactsModal();

  // animated appear based on index
  useEffect(() => {
    setTimeout(() => {
      setMounted(true);
    }, 200 * (showPromo ? index + 1 : index));
  }, []);

  // if contact is taken from localstorage item will be an email
  const email = typeof item === "string" ? item : item.contact_list_email;

  const contactFullname =
    typeof item !== "string" && item.contact_list_full_name;

  const name = contactFullname ? contactFullname + " " + email : email;

  // if drag equals email it means that the contact is being dragged
  const dragged = drag === email;

  const draggedOrHover = dragged || hover;

  // mobile condition behavior for contacts
  const touched = useRef<boolean>(false);

  const ref = useRef<HTMLButtonElement>(null);
  useOnClickOutside(ref, () => (touched.current = false));

  // hide outer circle on mobile when contact is clicked
  const hideElems = (hide: boolean) => {
    const circle = document.querySelector(".outer-circle");
    const tooltip = document.querySelector(".dropzone-tooltip-container");
    if (hide) {
      circle?.classList.add("hidden");
      tooltip?.classList.add("hidden");
      ref.current?.parentElement?.classList.add("circle-active");
    } else {
      circle?.classList.remove("hidden");
      tooltip?.classList.remove("hidden");
      ref.current?.parentElement?.classList.remove("circle-active");
    }
  };

  useEffect(() => {
    if (mobileCondition && draggedOrHover) {
      hideElems(true);
    }
    return () => {
      hideElems(false);
    };
  }, [dragged, hover]);

  const handleClick = (mail: string | boolean) => {
    if (mobileCondition) {
      if (!touched.current && !empty) {
        touched.current = true;
      } else {
        setRecipientEmail(mail);
      }
    } else {
      setRecipientEmail(mail);
    }
  };

  // handeling tooltip modals for manage contacts or explanation of contacts circle
  // showing once
  const handleShowModal = () => {
    const cookieKey = isLoggedIn()
      ? "manage-contact-tooltip-modal"
      : "send-to-contact-tooltip-modal";
    if (getCookie(cookieKey) !== "true") {
      setModal({
        modal: ManageContactsToolltipModal,
        container: VerificationContainer,
        props: { targetRef: ref.current, isManageContacts: isLoggedIn() },
      });
      setCookies(cookieKey, "true");
    }
  };

  const showVerificationContainer = () => {
    if (!isLoggedIn()) {
      if (!getModal()) {
        setModal({
          modal: ManageContactsToolltipModal,
          container: VerificationContainer,
          props: {
            targetRef: ref.current,
          },
        });
      } else {
        closeModal();
      }
      settingsClickCallback && settingsClickCallback(!!getModal());
    }
  };

  // logic to show manage contacts tooltip on mount
  useEffect(() => {
    // checking if its setting item and if its mounted
    if (
      settingItem &&
      mounted &&
      !mobileCondition &&
      shouldShowManageContactsModal
    ) {
      setTimeout(() => {
        showVerificationContainer();
        incTodaysViewCounts();

        setTimeout(() => {
          closeModal();
        }, 5000);
        // delay cos of animation
      }, 1000);
    }
  }, [mounted, mobileCondition, shouldShowManageContactsModal]);

  if (settingItem) {
    return (
      <AnimationContainer
        animationClassName="appear-animation"
        animationOn={mounted}
      >
        {isLoggedIn() ? (
          // @ts-ignore
          <LinkToNewTab
            isNewTab={true}
            page="user/contacts/contacts-list"
            classes={`contact item-${index + 1} manage-contacts`}
            ref={ref}
          >
            {/* @ts-ignore */}
            <Tooltip
              tooltipText={getTextByKey("manage-your-contacts-tooltip")}
              bottom
              margin={10}
            >
              <div className="contact-img">
                <Icon name="home/users-settings" blue />
              </div>
              <div className="contact-name"></div>
            </Tooltip>
          </LinkToNewTab>
        ) : (
          <button
            className={`contact item-${index + 1} manage-contacts`}
            ref={ref}
            aria-label="Manage contacts"
            onClick={showVerificationContainer}
          >
            <div className="contact-img">
              <Icon name="home/users-settings" blue />
            </div>
            <div className="contact-name"></div>
          </button>
        )}
      </AnimationContainer>
    );
  }

  if (empty) {
    return (
      <AnimationContainer
        animationClassName="appear-animation"
        animationOn={mounted}
      >
        <button
          className={`contact item-${index + 1} empty`}
          onClick={() => {
            !isLoggedIn() && handleClick(true);
          }}
          ref={ref}
          aria-label="Manage contacts"
        >
          {isLoggedIn() ? (
            <div onClick={addContact}>
              <ButtonBody />
            </div>
          ) : (
            <ButtonBody />
          )}
        </button>
      </AnimationContainer>
    );
  }

  return (
    <AnimationContainer
      animationClassName="appear-animation"
      animationOn={mounted}
    >
      <button
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={`contact item-${index + 1} ${
          draggedOrHover ? "dragged" : ""
        }`}
        data-email={email}
        data-name={item.contact_list_full_name}
        onClick={() => {
          handleClick(email);
        }}
        ref={ref}
        aria-label={`${!item.contact_list_image ? name.charAt(0) : ""} ${name}`}
      >
        <div
          className={`contact-img ${item.contact_list_image ? "" : "text-img"}`}
          style={{
            backgroundColor: getBackgroundColor(name),
          }}
        >
          {item.contact_list_image ? (
            <Image
              src={item.contact_list_image}
              className="contact-avatar"
              alt="user-avatar"
            />
          ) : (
            name.charAt(0)
          )}
        </div>
        <div className="contact-name">
          <span>
            {draggedOrHover && !mobileCondition ? (
              <div className="dragged-text">
                {getTextByKey("send-file-to-text")}
              </div>
            ) : null}
            <ContactNameElem {...{ draggedOrHover, item, email, name }} />
            {mobileCondition && draggedOrHover ? (
              <Button
                onClick={() => {
                  handleClick(email);
                }}
              >
                {getTextByKey("upload-files-text")}
              </Button>
            ) : null}
          </span>
        </div>
      </button>
      {draggedOrHover && mobileCondition ? (
        <div className="dragged-overflow" />
      ) : null}
    </AnimationContainer>
  );
};

export default ContactItem;
